import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	Modal,
	Card,
	CardContent,
	Typography,
	Grid,
	TextField,
} from "@material-ui/core";
import { ValidatorForm } from "react-material-ui-form-validator";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
} from "frame/components";
import { CardElement, injectStripe } from "react-stripe-elements";
import { getWebsiteBaseUrl } from "frame";
import {
	subscriptionUpdatePaymentSave,
	onAddPaymentClose,
	onStripeError,
	subscriptionModalSubmit,
	subscriptionUpdatePaymentSaveFail
} from "../../actions";

const useStyles = makeStyles((theme) => ({
	root: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		outline: "none",
		boxShadow: theme.shadows[20],
		width: 700,
		maxHeight: "100%",
		overflowY: "auto",
		maxWidth: "100%",
	},
	container: {
		marginTop: theme.spacing(3),
	},
	actions: {
		marginTop: theme.spacing(5),
		justifyContent: "flex-end",
	},
	stripeWrapper: {
		border: "solid 1px #D9D9D9",
		borderRadius: "5px",
		padding: "20px 10px 20px 10px",
		marginTop: "5px",
	},
	stripeWrapperRed: {
		border: "solid 1px",
		borderColor: theme.palette.error.main,
		borderRadius: "5px",
		padding: "20px 10px 20px 10px",
		marginTop: "5px",
	},
	stripeErrorLabel: {
		margin: "5px 20px",
		color: theme.palette.error.main,
	},
}));

const AddPaymemntForm = ({
	numberOfStaff,
	onSubmit,
	onClose,
	onUpdate,
	isAddPaymentModalShowing,
	onStripeResponseError,
	onSaveFail,
	stripe,
	isUpdate,
	currentOrganisationGUID,
	setSuccessMessage,
	setErrorMessage,
	organisationName,
	submitStatus,
	creditCardTokenErrorMessage,
}) => {
	const classes = useStyles();
	const [error, setError] = useState();
	const [billingPostcode, setBillingPostcode] = useState("");
	const [showBillingPostcode, setShowBillingPostcode] = useState(false);
	const friendlyPaymentErrorMessage =
		"Oops, something went wrong. Please try again or use a different card.";
	const requiresPostcodeMessage =
		"Please enter your billing postcode so we can calculate tax.";

	const requiresBillingPostcode = useMemo(() => {
		if (showBillingPostcode) return true;
		if (!creditCardTokenErrorMessage) return false;
		return (
			`${creditCardTokenErrorMessage}`.toLowerCase().includes("billing postcode") ||
			`${creditCardTokenErrorMessage}`.toLowerCase().includes("billing post code") ||
			`${creditCardTokenErrorMessage}`.toLowerCase().includes("postcode")
		);
	}, [creditCardTokenErrorMessage, showBillingPostcode]);

	useEffect(() => {
		if (requiresBillingPostcode && !showBillingPostcode) {
			setShowBillingPostcode(true);
		}
	}, [requiresBillingPostcode, showBillingPostcode]);

	// Reset postcode prompt between modal opens.
	// This ensures the postcode field only appears when Stripe specifically requires it,
	// not permanently after a single failed attempt.
	useEffect(() => {
		if (!isAddPaymentModalShowing) return;
		setShowBillingPostcode(false);
		setBillingPostcode("");
		setError(null);
	}, [isAddPaymentModalShowing]);

	// Hide postcode again after a successful save.
	useEffect(() => {
		if (submitStatus !== "done") return;
		setShowBillingPostcode(false);
		setBillingPostcode("");
		setError(null);
	}, [submitStatus]);

	if (!isAddPaymentModalShowing) {
		return null;
	}

	const onSubmitEffect = (event) => {
		event.preventDefault();

		if (requiresBillingPostcode && (!billingPostcode || billingPostcode.trim() === "")) {
			setError(requiresPostcodeMessage);
			onStripeResponseError(requiresPostcodeMessage);
			onSaveFail(requiresPostcodeMessage);
			return;
		}

		if (error) {
			onSaveFail(error);
			return;
		}

		onSubmit();
		// todo: remove promise, convert to effect
		stripe
			.createToken(
				requiresBillingPostcode
					? {
							name: organisationName,
							address_zip: billingPostcode.trim(),
							// Default NZ unless/ until AU is supported explicitly.
							address_country: "NZ",
					  }
					: { name: organisationName }
			)
			.then((res) => {
				if (res.error) {
					onStripeResponseError(friendlyPaymentErrorMessage);
					setError(friendlyPaymentErrorMessage);
					onSaveFail(friendlyPaymentErrorMessage);
				} else {
					// Always use the update endpoint - it supports both "add" and "update" flows.
					onUpdate({ token: res.token });
				}
			})
			.catch(() => {
				onSaveFail(friendlyPaymentErrorMessage);
				onStripeResponseError(friendlyPaymentErrorMessage);
				setError(friendlyPaymentErrorMessage);
			});
	};

	return (
		<Modal open={true} onClose={onClose}>
			<Card className={classes.root}>
				<ValidatorForm
					autoComplete="off"
					noValidate
					onSubmit={onSubmitEffect}
				>
					<CardContent>
						<Typography align="center" gutterBottom variant="h3">
							{isUpdate
								? "Update Credit Card"
								: "Add Payment Method"}
						</Typography>
						<Grid
							className={classes.container}
							container
							spacing={3}
						>
							<Grid item xs={12}>
								<div>
									<span>
										I agree to subscribe for the number of active users at the cost described at{" "}
									</span>
									<a
										href={`${getWebsiteBaseUrl()}/try-buy/`}
										title="Try Buy"
										target="_blank"
										rel="noopener noreferrer"
									>
										{getWebsiteBaseUrl()}/try-buy/.{" "}
									</a>
									<span>
										I agree to the terms and conditions{" "}
									</span>
									<a
										href={`${getWebsiteBaseUrl()}/terms-of-use/`}
										title="Terms of Use"
										target="_blank"
										rel="noopener noreferrer"
									>
										{getWebsiteBaseUrl()}/terms-of-use/
									</a>
									.
								</div>

								<div
									className={
										error
											? classes.stripeWrapperRed
											: classes.stripeWrapper
									}
								>
									{requiresBillingPostcode && (
										<TextField
											fullWidth
											variant="outlined"
											size="small"
											label="Billing Postcode"
											value={billingPostcode}
											onChange={(e) => setBillingPostcode(e.target.value)}
											style={{ marginBottom: 12 }}
										/>
									)}
									<CardElement
										// Hide Stripe's internal postcode field by default.
										// We show our own postcode input only when Stripe requires it.
										hidePostalCode={true}
										onChange={(e) => {
											if (e.error && e.error.message) {
												setError(e.error.message);
											}
											else {
												setError(null);
											}
										}}
									/>
								</div>

								{error && (
									<span className={classes.stripeErrorLabel}>
										{error}
									</span>
								)}
								{submitStatus === "error" ? (
									<span className={classes.stripeErrorLabel}>
										{creditCardTokenErrorMessage ||
											"There was a problem updating your credit card. Please contact support for more information."}
									</span>
								) : null}
							</Grid>
						</Grid>

						<FormActions className={classes.actions}>
							<FormSubmitButton
								loading={ submitStatus === "processing"}
								disabled={ submitStatus === "processing"}
							>
								Save
							</FormSubmitButton>
							<FormButton onClick={onClose}>Cancel</FormButton>
						</FormActions>
					</CardContent>
				</ValidatorForm>
			</Card>
		</Modal>
	);
};

AddPaymemntForm.propTypes = {
	numberOfStaff: PropTypes.number.isRequired,
	stripe: PropTypes.object,
	onSubmit: PropTypes.func,
	onClose: PropTypes.func,
	onUpdate: PropTypes.func,
	onStripeResponseError: PropTypes.func,
	onSaveFail: PropTypes.func,
	isAddPaymentModalShowing: PropTypes.bool,
	isUpdate: PropTypes.bool,
	setSuccessMessage: PropTypes.func,
	setErrorMessage: PropTypes.func,
	organisationName: PropTypes.string,
	currentOrganisationGUID: PropTypes.string,
	submitStatus: PropTypes.string,
	creditCardTokenErrorMessage: PropTypes.string,
};

const mapStateToProps = ({ subscription, staff, organisations, profile }) => ({
	numberOfStaff: staff.pagination.results.length,
	isAddPaymentModalShowing: subscription.isAddPaymentModalShowing,
	currentOrganisationGUID: organisations.currentOrganisation,
	organisationName: profile.organisation,
	submitStatus: subscription.flags.updateStatus,
	creditCardTokenErrorMessage: subscription.creditCardTokenErrorMessage,
});

export default injectStripe(
	connect(mapStateToProps, (dispatch) => ({
		onStripeResponseError: (message) => dispatch(onStripeError(message)),
		onSubmit: (payload) => dispatch(subscriptionModalSubmit(payload)),
		onClose: (payload) => dispatch(onAddPaymentClose(payload)),
		onUpdate: (payload) => dispatch(subscriptionUpdatePaymentSave(payload)),
		onSaveFail: (payload) => dispatch(subscriptionUpdatePaymentSaveFail(payload)),
	}))(AddPaymemntForm)
);
